import React from 'react';
import 'swiper/css';
import 'swiper/css/navigation';

import { articleService } from 'lib/services/ArticleService';
import {
  Field,
  GetStaticComponentProps,
  useComponentProps,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import { defaultLanguage } from 'temp/config';
import { ArticleCardData, ArticleData } from 'model/Article';
import ArticlesSectionComponent from './ArticlesSectionComponent';
import { useI18n } from 'next-localization';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import RelatedArticleTile from 'components/Tiles/RelatedArticleTile';

type RelatedArticlesSectionFields = {
  limitedDisplay: Field<string>;
  articleCategories: Field<string[]>;
  articleTags: Field<string[]>;
  articleThemes: Field<string[]>;
  articleCapabilities: Field<string[]>;
  viewAll?: {
    value: string;
  };
  baseUrl?: Field<string>;
};

type RelatedArticlesSectionProps = ComponentProps & {
  fields?: RelatedArticlesSectionFields;
  heading?: React.ReactNode;
};

const RelatedArticlesSection: React.FC<RelatedArticlesSectionProps> = ({ rendering }) => {
  const articles = useComponentProps<ArticleCardData[]>(rendering.uid);
  const { t } = useI18n();
  const {
    sitecoreContext: { articleWildcardConfiguration },
  } = useSitecoreContext();

  if (!articles?.length) return null;

  return (
    <ArticlesSectionComponent
      rendering={rendering}
      articles={articles}
      viewAllSrc={articleWildcardConfiguration?.baseUrl}
      sectionTitle={t('relatedArticlesTitle')}
      backgroundColor={rendering.params?.backgroundColor}
      sliderCardComponent={RelatedArticleTile}
    />
  );
};

export const getStaticProps: GetStaticComponentProps = async (rendering, layoutData) => {
  const fields = rendering.fields as unknown as RelatedArticlesSectionFields;
  const siteId = layoutData.sitecore.context?.siteRoot;
  const language = layoutData.sitecore.context.language ?? defaultLanguage;
  // Get article data hacked to the context by 'src/lib/page-props-factory/plugins/articles.ts'
  const articleData = layoutData.sitecore.context.article as ArticleData;
  if (!fields && !articleData) return [];
  if (articleData) {
    // We are on an article page, fetch the articles related to that article.
    return await articleService.getRelatedArticlesByPage(
      language,
      siteId,
      articleData.id,
      articleData.limitedDisplay.targetId!,
      articleData.articleCapabilities?.targetIds ?? [],
      articleData.articleCategory.targetItem?.id ?? ''
    );
  }

  // Non-article page, fetch articles based on data passed in layout data.
  const articles = await articleService.getRelatedArticles(
    language,
    siteId,
    fields.limitedDisplay.value,
    fields.articleCapabilities.value ?? [],
    fields.articleCategories.value ?? [],
    fields.articleTags.value ?? [],
    fields.articleThemes.value ?? []
  );
  return articles;
};
export default RelatedArticlesSection;
